import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import { useTranslation, Trans } from 'react-i18next';
import { AppContext } from '../context/context';
import { setToken, resolveRoleFromDecoded } from '../components/authentication/functions/authentication_management';
import type { DecodedAuthToken } from '../components/authentication/types/AuthTypes';
import {
  getInvitationDetails,
  acceptInvitation,
  readAxiosDetail,
  type InvitationDetails,
} from '../components/company/api_calls';

export default function RegisterPage() {
  const { t } = useTranslation(['auth', 'navigation']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setFetchError(t('auth:register.noTokenProvided'));
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const details = await getInvitationDetails(token);
        if (!cancelled) setInvitation(details);
      } catch (e) {
        if (!cancelled) setFetchError(readAxiosDetail(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, t]);

  const handleSubmit = async () => {
    setSubmitError(null);

    if (!fullName.trim()) {
      setSubmitError(t('auth:register.fullNameRequired'));
      return;
    }
    if (!password) {
      setSubmitError(t('auth:register.passwordRequired'));
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError(t('auth:register.passwordsDoNotMatch'));
      return;
    }

    setSubmitting(true);
    try {
      const result = await acceptInvitation(token, {
        full_name: fullName.trim(),
        password,
      });

      // Store JWT and log the user in
      setToken(result.access_token);
      const decoded = jwtDecode<DecodedAuthToken>(result.access_token);

      if (context?.userData?.setUserDetails) {
        context.userData.setUserDetails({
          email: decoded.sub,
          userId: decoded.user_id,
          fullName: fullName.trim(),
          role: resolveRoleFromDecoded(decoded),
          customerId: decoded.customer_id ?? null,
          customerName: decoded.customer_name ?? null,
          companyRole: decoded.company_role ?? null,
        });
      }

      navigate('/', { replace: true });
    } catch (e) {
      const msg = readAxiosDetail(e);
      if (msg.toLowerCase().includes('already exists')) {
        setSubmitError(t('auth:register.accountExists'));
      } else if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('accepted')) {
        setInvitation((prev) => prev ? { ...prev, is_expired: true } : prev);
      } else {
        setSubmitError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const roleLabel = invitation?.company_role === 'company_admin'
    ? t('navigation:userMenu.companyAdmin')
    : t('navigation:userMenu.companyMember');

  // Loading state
  if (loading) {
    return (
      <CenteredContainer>
        <CircularProgress />
        <Typography color="text.secondary" sx={{ mt: 2 }}>{t('auth:register.loadingInvitation')}</Typography>
      </CenteredContainer>
    );
  }

  // Error fetching invitation
  if (fetchError) {
    return (
      <CenteredContainer>
        <Paper sx={{ p: 4, maxWidth: 440, width: '100%', textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>{t('auth:register.invitationNotFoundTitle')}</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {fetchError}
          </Typography>
          <Button variant="outlined" component={RouterLink} to="/">
            {t('auth:register.goToLogin')}
          </Button>
        </Paper>
      </CenteredContainer>
    );
  }

  // Expired / already used
  if (invitation?.is_expired) {
    return (
      <CenteredContainer>
        <Paper sx={{ p: 4, maxWidth: 440, width: '100%', textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>{t('auth:register.invitationExpiredTitle')}</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t('auth:register.invitationExpiredMessage')}
          </Typography>
          <Button variant="outlined" component={RouterLink} to="/">
            {t('auth:register.goToLogin')}
          </Button>
        </Paper>
      </CenteredContainer>
    );
  }

  // Registration form
  return (
    <CenteredContainer>
      <Paper sx={{ p: 4, maxWidth: 440, width: '100%' }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t('auth:register.joinHeading', { companyName: invitation?.customer_name ?? '' })}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          <Trans
            i18nKey="register.joinSubheading"
            t={t}
            ns="auth"
            values={{ companyName: invitation?.customer_name ?? '' }}
            components={{ bold: <strong /> }}
          />
        </Typography>
        <Chip
          label={roleLabel}
          size="small"
          color={invitation?.company_role === 'company_admin' ? 'primary' : 'default'}
          variant="outlined"
          sx={{ mb: 3 }}
        />

        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)} sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        <TextField
          fullWidth
          label={t('auth:register.emailLabel')}
          value={invitation?.email || ''}
          disabled
          size="small"
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label={t('auth:register.fullNameLabel')}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          size="small"
          required
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label={t('auth:register.passwordLabel')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          size="small"
          required
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label={t('auth:register.confirmPasswordLabel')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          size="small"
          required
          sx={{ mb: 3 }}
        />

        <Button
          fullWidth
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={20} /> : t('auth:register.submitButton')}
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          {t('auth:register.alreadyHaveAccountPrefix')}{' '}
          <RouterLink to="/" style={{ color: 'inherit' }}>{t('auth:register.logInLink')}</RouterLink>
        </Typography>
      </Paper>
    </CenteredContainer>
  );
}

function CenteredContainer({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      {children}
    </Box>
  );
}
